import React, { useEffect, useRef } from 'react';

/**
 * 필드 하이라이트 오버레이 (Canvas 기반)
 * - iframe PDF 뷰어 위에 Canvas로 하이라이트 렌더링
 */
export default function FieldHighlightOverlay({ 
  boundingBoxes = [], 
  highlightedField = null,
  fieldValueMap = {},
  imageWidth = 800, 
  imageHeight = 1131, 
  visible = true,
  autoScroll = false
}) {
  const canvasRef = useRef(null);

  // Current field value
  const fieldValue = fieldValueMap[highlightedField];

  // Find bounding boxes matching the field value
  const matchedBoxes = React.useMemo(() => {
    if (!visible || !highlightedField || !boundingBoxes || boundingBoxes.length === 0 || !fieldValue) {
      return [];
    }

    return boundingBoxes.filter(box => {
      const boxText = box.text?.trim().replace(/\s+/g, '');
      const fieldText = String(fieldValue).trim().replace(/\s+/g, '');
      
      // Exact match or partial match
      return boxText === fieldText || 
             boxText.includes(fieldText) || 
             fieldText.includes(boxText);
    });
  }, [visible, highlightedField, boundingBoxes, fieldValue]);

  // Canvas 하이라이트 렌더링
  useEffect(() => {
    if (!canvasRef.current || matchedBoxes.length === 0) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Canvas 크기 설정 (부모 컨테이너 크기에 맞춤)
    const rect = canvas.parentElement?.getBoundingClientRect();
    if (rect) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    // 이전 그리기 지우기
    context.clearRect(0, 0, canvas.width, canvas.height);

    // 스케일 계산 (바운딩 박스 좌표 → Canvas 좌표)
    const scaleX = canvas.width / imageWidth;
    const scaleY = canvas.height / imageHeight;

    console.log(`🎨 Canvas 하이라이트 렌더링: ${highlightedField} = ${fieldValue}`);
    console.log(`📐 Canvas: ${canvas.width}x${canvas.height}, PDF: ${imageWidth}x${imageHeight}, Scale: ${scaleX.toFixed(2)}x${scaleY.toFixed(2)}`);

    // 하이라이트 그리기
    matchedBoxes.forEach((box, index) => {
      const [x1, y1, x2, y2] = box.box;
      
      // 좌표 스케일링
      const scaledX = x1 * scaleX;
      const scaledY = y1 * scaleY;
      const scaledWidth = (x2 - x1) * scaleX;
      const scaledHeight = (y2 - y1) * scaleY;

      // 노란색 반투명 배경
      context.fillStyle = 'rgba(255, 215, 0, 0.4)';
      context.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
      
      // 노란색 테두리
      context.strokeStyle = 'rgba(255, 215, 0, 0.9)';
      context.lineWidth = 3;
      context.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

      // 필드명 라벨 (첫 번째 박스에만)
      if (index === 0) {
        context.fillStyle = 'rgba(255, 215, 0, 1)';
        context.fillRect(scaledX, scaledY - 24, 150, 24);
        
        context.fillStyle = 'black';
        context.font = 'bold 12px Arial';
        context.fillText(highlightedField, scaledX + 5, scaledY - 8);
      }

      console.log(`  📦 박스 ${index + 1}: [${scaledX.toFixed(0)}, ${scaledY.toFixed(0)}, ${scaledWidth.toFixed(0)}, ${scaledHeight.toFixed(0)}]`);
    });

    console.log(`✅ ${matchedBoxes.length}개 하이라이트 렌더링 완료`);
  }, [matchedBoxes, highlightedField, fieldValue, imageWidth, imageHeight]);

  // Early return after all Hook calls
  if (!visible || !highlightedField || matchedBoxes.length === 0) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-10"
      style={{ 
        mixBlendMode: 'multiply'
      }}
    />
  );
}
